import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toolsData } from '../Data/ToolsData';
import { ArrowLeft, Box, LayoutGrid } from 'lucide-react';

/**
 * MORETOOLS WRAPPER
 * A clean, minimalist layout for all individual tool pages.
 */
const ToolWrapper = ({ children, title, category }) => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex flex-col font-sans">
      {/* TOP NAVIGATION */}
      <header className="border-b border-slate-900 bg-slate-950/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex justify-between items-center">
          
          <div className="flex items-center gap-4">
            <Link 
              to="/tools" 
              className="p-2 hover:bg-slate-900 rounded-lg transition-colors text-slate-400 hover:text-white"
              title="Back to All Tools"
            >
              <ArrowLeft size={20} />
            </Link>
            <div className="h-6 w-[1px] bg-slate-800" />
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-cyan-500 uppercase tracking-widest leading-none mb-1">
                {category || 'Utility'}
              </span>
              <h1 className="text-lg font-bold text-white leading-none">
                {title}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
             <span className="hidden sm:block text-[10px] font-medium text-slate-500 uppercase tracking-tighter">
               MoreTools Ecosystem
             </span>
             <Box size={16} className="text-slate-700" />
          </div>
        </div>
      </header>

      {/* TOOL CONTENT AREA */}
      <main className="flex-1 py-8 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-slate-900/40 border border-slate-800/60 rounded-2xl shadow-xl overflow-hidden min-h-[70vh]">
            <div className="p-6 sm:p-10">
              {children}
            </div>
          </div>
        </div>
      </main>

      {/* SIMPLE SYSTEM FOOTER */}
      <footer className="py-6 border-t border-slate-900">
        <div className="max-w-5xl mx-auto px-6 flex justify-between items-center">
          <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest">
            © 2026 MORETOOLS // ALL RIGHTS RESERVED
          </p>
          <div className="flex gap-4">
            <div className="w-2 h-2 rounded-full bg-green-500/50 animate-pulse" title="System Online" />
          </div>
        </div>
      </footer>
    </div>
  );
};

/**
 * DYNAMIC TOOL LOADER
 */
export const DynamicToolLoader = () => {
  const { toolPath } = useParams();
  
  // Find tool efficiently
  const tool = useMemo(() => 
    toolsData.find(t => t.url === `/tools/${toolPath}`), 
    [toolPath]
  );

  if (!tool) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
        <LayoutGrid size={48} className="text-slate-800 mb-6" />
        <h1 className="text-2xl font-black mb-2 uppercase">Tool Not Found</h1>
        <p className="text-slate-500 text-sm mb-8">The requested module does not exist in the MoreTools library.</p>
        <Link to="/tools" className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-xs font-bold transition-all">
          BACK TO TOOLS
        </Link>
      </div>
    );
  }

  return (
    <ToolWrapper title={tool.title} category={tool.category}>
      {tool.component ? tool.component : (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 mb-6">
            <Box size={32} className="text-slate-700 animate-bounce" />
          </div>
          <h2 className="text-xl font-bold text-white uppercase mb-2">Developing Module</h2>
          <p className="text-slate-500 text-sm max-w-xs">
            The <span className="text-cyan-500 font-bold">{tool.title}</span> utility is currently being integrated into the MoreTools dashboard.
          </p>
        </div>
      )}
    </ToolWrapper>
  );
};